import type { CreateRoundCommandPayload } from "../../../domain/round/round-reducer.js";
import type { CourseSnapshot } from "../../../domain/round/round-types.js";
import { simulatorRules } from "./simulator-setup-model.js";
import { parseHandicapIndex } from "../../../shared/handicap-input.js";
import { wizardTime } from "./wizard-time.js";
import {
  playerTags,
  selectHoles,
  stakeSnapshot,
  type WizardState,
} from "./wizard-state.js";

export function usesV3Scoring(state: WizardState): boolean {
  return (
    (state.kind === "outdoor" || state.kind === "simulator") &&
    ["stroke-play", "match-play", "stableford"].includes(state.scoring)
  );
}

/** Golf's +2 notation means two strokes given back, stored as PH -2. */
export function parsePlayingHandicap(value: string | undefined): number | null {
  const raw = value?.trim() ?? "";
  if (!/^[+-]?\d+$/.test(raw)) return null;
  const parsed = raw.startsWith("+") ? -Number(raw.slice(1)) : Number(raw);
  return Number.isInteger(parsed) && parsed >= -20 && parsed <= 100
    ? parsed
    : null;
}

export function validatePlayingHandicaps(state: WizardState): string | null {
  if (!state.handicap) return null;
  for (const player of state.players) {
    if (player.handicap.trim() && parseHandicapIndex(player.handicap) === null)
      return `Use a handicap from -10.0 to 54.0 for ${player.displayName}, with a comma or dot.`;
    const raw = player.playingHandicap?.trim() ?? "";
    const ph = parsePlayingHandicap(raw);
    if (raw && ph === null)
      return `Use a whole number of strokes for ${player.displayName}.`;
    if (state.handicap && ph === null)
      return `Enter strokes for ${player.displayName}.`;
  }
  return null;
}

export function buildV3SetupInput(
  state: WizardState,
  course: CourseSnapshot,
  playerCourses: Record<string, CourseSnapshot> = {},
): CreateRoundCommandPayload {
  const problem = validatePlayingHandicaps(state);
  if (problem) throw new Error(problem);
  const holeNumbers = selectHoles(
    course.holes,
    state.holeSet,
    state.customHoles,
  ).map((hole) => hole.number);
  if (!holeNumbers.length)
    throw new Error(
      "This tee has no holes in the selected set. Choose another hole set.",
    );
  if (
    state.scoring !== "stroke-play" &&
    state.scoring !== "match-play" &&
    state.scoring !== "stableford"
  )
    throw new Error(
      "This scoring format is not supported by this round agreement.",
    );
  if (state.scoring === "match-play" && state.players.length !== 2)
    throw new Error("Match Play needs two players.");
  const agreedAt = new Date().toISOString();
  const participants = buildV3Participants(
    state,
    course,
    playerCourses,
    holeNumbers,
    agreedAt,
  );
  const self = state.players.find((player) => player.you);
  const selfTags = self ? playerTags(state, self.id) : [];
  return {
    schemaVersion: 3,
    orderedHoleNumbers: holeNumbers,
    scorekeeperGeneration: 0,
    participants,
    course,
    mainContest: {
      id: crypto.randomUUID(),
      format: state.scoring,
      scoringBasis: state.handicap ? "net" : "gross",
      sides: participants.map((player) => ({
        id: player.id,
        kind: "person",
        participantIds: [player.id],
      })),
      holeNumbers,
      scope: "round",
      rulesVersion: 1,
      lockedAt: null,
    },
    rules: golfRules(state, state.scoring),
    conditions: {
      ...wizardTime(state),
      cart: selfTags.includes("cart")
        ? true
        : selfTags.includes("walking")
          ? false
          : null,
      weather: state.weather,
    },
  };
}

function buildV3Participants(
  state: WizardState,
  course: CourseSnapshot,
  playerCourses: Record<string, CourseSnapshot>,
  holeNumbers: number[],
  agreedAt: string,
) {
  return state.players.map((player) => {
    const tee = playerCourses[player.id] ?? course;
    if (
      player.teeId &&
      player.teeId !== state.course?.teeId &&
      !playerCourses[player.id]
    )
      throw new Error(
        `Load ${player.displayName}'s selected tee before starting.`,
      );
    if (
      tee.id !== course.id ||
      holeNumbers.some(
        (number) => !tee.holes.some((hole) => hole.number === number),
      )
    )
      throw new Error(
        `${player.displayName}'s tee must include every selected hole at this course.`,
      );
    const playingHandicap = state.handicap
      ? parsePlayingHandicap(player.playingHandicap)
      : null;
    return {
      id: player.id,
      displayName: player.displayName,
      handicapIndex: parseHandicapIndex(player.handicap),
      teamId: null,
      tags: playerTags(state, player.id),
      teeSnapshot: {
        courseId: tee.id,
        teeId: capturedTeeId(state, player.teeId),
        teeName: tee.teeName,
        rating: tee.rating,
        slope: tee.slope,
        holes: tee.holes,
        source: tee.source,
      },
      handicapSnapshot: {
        playingHandicap,
        appliesToHoleNumbers: holeNumbers,
        source: "agreed" as const,
        agreedAt: playingHandicap === null ? null : agreedAt,
      },
    };
  });
}

function golfRules(
  state: WizardState,
  format: "stroke-play" | "match-play" | "stableford",
): CreateRoundCommandPayload["rules"] {
  return {
    kind: state.kind,
    format,
    handicapMode: state.handicap
      ? state.scoring === "match-play"
        ? "relative"
        : "full"
      : "off",
    metric: null,
    stake: state.stake ? stakeSnapshot(state.stake) : null,
    ...(state.kind === "simulator"
      ? {
          simulator: simulatorRules(state),
          rivalrySummary: state.rivalrySummary ?? "simulator",
        }
      : {}),
  };
}

function capturedTeeId(
  state: WizardState,
  teeId: string | null | undefined,
): string | null {
  const selected =
    state.teeChoices?.find((tee) => tee.teeId === teeId) ?? state.course;
  return selected?.kind === "captured"
    ? (selected.capturedTeeId ?? null)
    : (teeId ?? state.course?.teeId ?? null);
}
